import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { api } from "@/lib/api";
import { translate, statusLabel, monthLabel } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const { user } = useAuth();
  const [lang, setLangState] = useState(() => localStorage.getItem("ch_lang") || "fr");

  // Sync from the logged-in user's saved preference
  useEffect(() => {
    if (user && user.lang && user.lang !== lang) {
      setLangState(user.lang);
      localStorage.setItem("ch_lang", user.lang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const setLang = useCallback((next) => {
    setLangState(next);
    localStorage.setItem("ch_lang", next);
    api.patch("/auth/language", { lang: next }).catch(() => {});
  }, []);

  const t = useCallback((key) => translate(lang, key), [lang]);
  const st = useCallback((status) => statusLabel(lang, status), [lang]);
  const ml = useCallback((month, year) => monthLabel(lang, month, year), [lang]);
  const locale = lang === "en" ? "en-US" : "fr-FR";
  const money = useCallback(
    (n) => new Intl.NumberFormat(locale, { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Number(n || 0)),
    [locale]
  );
  const date = useCallback((d) => (d ? new Date(d).toLocaleDateString(locale) : ""), [locale]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, st, ml, money, date }}>{children}</LanguageContext.Provider>
  );
}

export const useLang = () => useContext(LanguageContext);
